import React, { useEffect, useState, useMemo } from "react";
import "../lastProjects/LastProjects.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { resolveUrl } from "../../../utils/storeUtils";

const LastProducts = () => {
  const [products, setProducts] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [imagesZipLoading, setImagesZipLoading] = useState(false);
  const [socialExportLoading, setSocialExportLoading] = useState(false);
  const [showSocialDateRange, setShowSocialDateRange] = useState(false);
  const [socialStartDate, setSocialStartDate] = useState("");
  const [socialEndDate, setSocialEndDate] = useState("");
  const [serialCounters, setSerialCounters] = useState([]);
  const [serialCountersLoading, setSerialCountersLoading] = useState(false);

  // Filters
  const [searchName, setSearchName] = useState("");
  const [searchSerial, setSearchSerial] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStock, setFilterStock] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [showDuplicates, setShowDuplicates] = useState(false);

  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();
  const location = useLocation();
  const isStoreContext = location.pathname.startsWith("/dashboard/store");
  const productsEndpoint = isStoreContext ? "/store/products" : "/products";
  const editBasePath = isStoreContext
    ? "/dashboard/store/edit-product"
    : "/dashboard/edit-product";

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(productsEndpoint);
        const responseData = response?.data;
        const productsList = Array.isArray(responseData)
          ? responseData
          : responseData?.products || [];
        productsList.sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
        );
        setProducts(productsList);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [productsEndpoint]);

  useEffect(() => {
    if (!isStoreContext || !token) return;
    const fetchCounters = async () => {
      try {
        setSerialCountersLoading(true);
        const { data } = await axios.get("/store/products/serial-counters", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSerialCounters(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching serial counters:", error);
        setSerialCounters([]);
      } finally {
        setSerialCountersLoading(false);
      }
    };
    fetchCounters();
  }, [isStoreContext, token]);

  // Unique categories and years for dropdowns
  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return [...set].sort();
  }, [products]);

  const years = useMemo(() => {
    const set = new Set(
      products
        .map((p) => p.createdAt && new Date(p.createdAt).getFullYear())
        .filter(Boolean)
    );
    return [...set].sort((a, b) => b - a);
  }, [products]);

  // Which product IDs are duplicates (by name or serial)
  const duplicateIds = useMemo(() => {
    const nameCounts = {};
    const serialCounts = {};
    products.forEach((p) => {
      const n = (p.name || "").toLowerCase().trim();
      const s = (p.sku || "").toLowerCase().trim();
      if (n) nameCounts[n] = (nameCounts[n] || 0) + 1;
      if (s) serialCounts[s] = (serialCounts[s] || 0) + 1;
    });
    return new Set(
      products
        .filter((p) => {
          const n = (p.name || "").toLowerCase().trim();
          const s = (p.sku || "").toLowerCase().trim();
          return (n && nameCounts[n] > 1) || (s && serialCounts[s] > 1);
        })
        .map((p) => p._id)
    );
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (searchName)
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(searchName.toLowerCase())
      );
    if (searchSerial)
      list = list.filter((p) =>
        (p.sku || "").toLowerCase().includes(searchSerial.toLowerCase())
      );
    if (filterCategory)
      list = list.filter((p) => p.category === filterCategory);
    if (filterYear)
      list = list.filter(
        (p) =>
          p.createdAt &&
          new Date(p.createdAt).getFullYear() === Number(filterYear)
      );
    if (filterStock === "in-stock")
      list = list.filter((p) => typeof p.stock_quantity === "number" && p.stock_quantity >= 5);
    if (filterStock === "low-stock")
      list = list.filter((p) => typeof p.stock_quantity === "number" && p.stock_quantity > 0 && p.stock_quantity < 5);
    if (filterStock === "out-of-stock")
      list = list.filter((p) => typeof p.stock_quantity === "number" && p.stock_quantity === 0);
    if (showDuplicates) list = list.filter((p) => duplicateIds.has(p._id));
    if (sortBy === "name-asc")
      list = [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "name-desc")
      list = [...list].sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    else if (sortBy === "price-asc")
      list = [...list].sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    else if (sortBy === "price-desc")
      list = [...list].sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    return list;
  }, [products, searchName, searchSerial, filterCategory, filterYear, filterStock, sortBy, showDuplicates, duplicateIds]);

  const hasActiveFilter =
    searchName || searchSerial || filterCategory || filterYear || filterStock || sortBy || showDuplicates;

  const clearFilters = () => {
    setSearchName("");
    setSearchSerial("");
    setFilterCategory("");
    setFilterYear("");
    setFilterStock("");
    setSortBy("");
    setShowDuplicates(false);
  };

  const handleDeleteConfirm = (productId) => {
    setSelectedProductId(productId);
    setShowConfirmation(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${productsEndpoint}/${selectedProductId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProducts(
        products.filter((product) => product._id !== selectedProductId)
      );
      toast.success("Product has been deleted..!");
      setShowConfirmation(false);
      setSelectedProductId(null);
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while deleting."
      );
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return "-";
    return `${parsedDate.getDate()}/${parsedDate.getMonth() + 1}/${parsedDate.getFullYear()}`;
  };

  const getProductImageData = (product) => {
    const firstStoreImage = Array.isArray(product?.images)
      ? product.images[0]
      : null;
    const legacyImage = product?.image;
    const imageUrl = firstStoreImage?.url || legacyImage?.url || "";
    const imageAlt =
      firstStoreImage?.alt ||
      firstStoreImage?.arAlt ||
      legacyImage?.alt ||
      legacyImage?.arAlt ||
      product?.name ||
      "Product image";

    return { imageUrl, imageAlt };
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    if (!isStoreContext) return;
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setExportLoading(true);
      const res = await axios.get("/store/products/export/excel", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename =
        match?.[1]?.trim() || `store-products-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlob(res.data, filename);
      toast.success("Excel download started.");
    } catch (error) {
      console.error("Error exporting store products:", error);
      toast.error(
        error?.response?.data?.message || "Could not export store products to Excel."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadImagesZip = async () => {
    if (!isStoreContext) return;
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setImagesZipLoading(true);
      const res = await axios.get("/store/products/export/images-zip", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename=\"?([^\";]+)\"?/i);
      const filename =
        match?.[1]?.trim() || `store-products-images-${new Date().toISOString().slice(0, 10)}.zip`;
      downloadBlob(res.data, filename);
      toast.success("Images ZIP download started.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not download products images ZIP.");
    } finally {
      setImagesZipLoading(false);
    }
  };

  const handleExportSocialExcel = async () => {
    if (!isStoreContext) return;
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setSocialExportLoading(true);
      const params = new URLSearchParams();
      if (socialStartDate) params.set("startDate", socialStartDate);
      if (socialEndDate) params.set("endDate", socialEndDate);
      const qs = params.toString();
      const res = await axios.get(
        `/store/products/export/social-excel${qs ? `?${qs}` : ""}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename =
        match?.[1]?.trim() || `store-products-social-${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlob(res.data, filename);
      toast.success("Excel download started.");
    } catch (error) {
      console.error("Error exporting social media excel:", error);
      toast.error(error?.response?.data?.message || "Could not export products to Excel.");
    } finally {
      setSocialExportLoading(false);
    }
  };

  const apiBaseUrl = axios.defaults.baseURL || "";

  if (loading) {
    return (
      <div className="loader-container">
        <BeatLoader />
      </div>
    );
  }

  return (
    <>
      <header className="lastProjectsHeader"></header>
      <main className="lastProjects lastProjects--products">
        <div className="container">
          <div className="title">
            <div className="title-heading">
              <h1>{isStoreContext ? "Store Products" : "Last Products"}</h1>
              <span className="title-count">
                {hasActiveFilter
                  ? `${filteredProducts.length} / ${products.length}`
                  : `${products.length} ${products.length === 1 ? "item" : "items"}`}
              </span>
            </div>
            {isStoreContext ? (
              <div className="store-products-toolbar">
                <button
                  type="button"
                  className="store-products-export-btn"
                  onClick={handleExportExcel}
                  disabled={loading || exportLoading || imagesZipLoading}
                >
                  {exportLoading ? "Exporting..." : "Download Excel"}
                </button>
                <button
                  type="button"
                  className="store-products-export-btn store-products-export-btn--secondary"
                  onClick={handleDownloadImagesZip}
                  disabled={loading || imagesZipLoading || exportLoading}
                >
                  {imagesZipLoading ? "Preparing..." : "Download Images ZIP"}
                </button>
                <button
                  type="button"
                  className="store-products-export-btn store-products-export-btn--secondary"
                  onClick={() => setShowSocialDateRange((v) => !v)}
                  disabled={loading}
                >
                  Social Media
                </button>
              </div>
            ) : null}
          </div>
          {isStoreContext && showSocialDateRange ? (
            <div className="store-products-social-panel">
              <div className="store-products-social-field">
                <label>From</label>
                <input
                  type="date"
                  value={socialStartDate}
                  onChange={(e) => setSocialStartDate(e.target.value)}
                />
              </div>
              <div className="store-products-social-field">
                <label>To</label>
                <input
                  type="date"
                  value={socialEndDate}
                  onChange={(e) => setSocialEndDate(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="store-products-export-btn"
                onClick={handleExportSocialExcel}
                disabled={socialExportLoading}
              >
                {socialExportLoading ? "Exporting..." : "Download Excel"}
              </button>
              {(socialStartDate || socialEndDate) && (
                <button
                  type="button"
                  className="store-products-social-clear"
                  onClick={() => {
                    setSocialStartDate("");
                    setSocialEndDate("");
                  }}
                >
                  Clear dates
                </button>
              )}
            </div>
          ) : null}
          {isStoreContext ? (
            <section className="store-serial-counters-panel" style={{ marginBottom: "20px" }}>
              <h2 style={{ fontSize: "1.1rem", marginBottom: "10px", color: "#3d322b" }}>
                Latest product serials
              </h2>
              {serialCountersLoading ? (
                <p style={{ color: "#8a7a6d" }}>Loading counters…</p>
              ) : serialCounters.length === 0 ? (
                <p style={{ color: "#8a7a6d" }}>No serial counters yet.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.9rem",
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "2px solid #e8dfd5" }}>
                        <th style={{ padding: "8px" }}>Group</th>
                        <th style={{ padding: "8px" }}>Year</th>
                        <th style={{ padding: "8px" }}>Last serial</th>
                        <th style={{ padding: "8px" }}>Categories</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serialCounters.map((row) => (
                        <tr key={`${row.groupCode}-${row.year}`} style={{ borderBottom: "1px solid #f0e8df" }}>
                          <td style={{ padding: "8px", fontWeight: 600 }}>{row.groupCode}</td>
                          <td style={{ padding: "8px" }}>20{String(row.year).padStart(2, "0")}</td>
                          <td style={{ padding: "8px" }}>
                            <code style={{ fontFamily: "ui-monospace, monospace", letterSpacing: "0.05em" }}>
                              {row.lastSku}
                            </code>
                          </td>
                          <td style={{ padding: "8px", color: "#8a7a6d" }}>
                            {(row.categoryLabels || []).join(", ") || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}

          {isStoreContext && (
            <div className="products-filter-bar">
              <div className="store-products-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </div>
              <div className="store-products-search">
                <i className="fa-solid fa-barcode" />
                <input
                  type="text"
                  placeholder="Search by serial…"
                  value={searchSerial}
                  onChange={(e) => setSearchSerial(e.target.value)}
                />
              </div>
              <div className="store-products-filters">
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                >
                  <option value="">All years</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={filterStock}
                  onChange={(e) => setFilterStock(e.target.value)}
                >
                  <option value="">All stock</option>
                  <option value="in-stock">In stock</option>
                  <option value="low-stock">Low stock</option>
                  <option value="out-of-stock">Out of stock</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="">Sort: Default</option>
                  <option value="name-asc">Name A → Z</option>
                  <option value="name-desc">Name Z → A</option>
                  <option value="price-asc">Price: Low → High</option>
                  <option value="price-desc">Price: High → Low</option>
                </select>
              </div>
              <button
                type="button"
                className={`products-duplicates-btn${showDuplicates ? " products-duplicates-btn--active" : ""}`}
                onClick={() => setShowDuplicates((v) => !v)}
                title="Show duplicate products by name or serial"
              >
                <i className="fa-regular fa-copy" />
                {showDuplicates ? `Duplicates (${duplicateIds.size})` : "Show duplicates"}
              </button>
              {hasActiveFilter && (
                <button
                  type="button"
                  className="products-clear-filter-btn"
                  onClick={clearFilters}
                >
                  <i className="fa-solid fa-xmark" /> Clear
                </button>
              )}
            </div>
          )}

          <div className="table-container">
            <div
              className={`table table--products${isStoreContext ? " table--store-products" : ""}`}
            >
              <div className="head">
                <div className="col">
                  <div className="col-id">ID</div>
                  <div>Photo</div>
                  <div>Name</div>
                  {isStoreContext ? <div>Serial</div> : null}
                  <div>Category</div>
                  <div>Price</div>
                  <div>Created At</div>
                  <div className="col-actions">Actions</div>
                </div>
              </div>
              <div className="body">
                {filteredProducts.length === 0 ? (
                  <div className="list-empty">
                    <i className="fa-regular fa-box-open" />
                    <p>{hasActiveFilter ? "No products match your filters." : "No products found yet."}</p>
                  </div>
                ) : (
                  filteredProducts.map((product, index) => {
                    const { imageUrl, imageAlt } = getProductImageData(product);
                    const imgSrc = imageUrl ? resolveUrl(String(imageUrl), apiBaseUrl) : "";
                    const isDuplicate = duplicateIds.has(product._id);
                    return (
                      <div
                        className={`col row${isDuplicate && showDuplicates ? " row--duplicate" : ""}`}
                        key={product?._id || index}
                      >
                        <div className="col-id">#{index + 1}</div>
                        <div className="col-photo">
                          {imgSrc ? (
                            <img
                              src={imgSrc}
                              alt={imageAlt}
                              className="table-product-thumb"
                              loading="lazy"
                            />
                          ) : (
                            <span className="table-product-thumb-icon">
                              <i className="fa-regular fa-image" />
                            </span>
                          )}
                        </div>
                        <div className="col-name" title={product?.name || ""}>
                          <span>{product?.name || "-"}</span>
                          {isStoreContext &&
                          typeof product.stock_quantity === "number" &&
                          product.stock_quantity > 0 &&
                          product.stock_quantity < 5 ? (
                            <span className="store-admin-low-stock" title="Fewer than 5 units">
                              Low stock ({product.stock_quantity})
                            </span>
                          ) : null}
                          {isStoreContext &&
                          typeof product.stock_quantity === "number" &&
                          product.stock_quantity === 0 ? (
                            <span className="store-admin-out-stock">Out of stock</span>
                          ) : null}
                        </div>
                        {isStoreContext ? (
                          <div className="col-serial">
                            {product?.sku ? (
                              <code className="store-admin-product-sku">{product.sku}</code>
                            ) : (
                              <span className="store-admin-sku-missing">—</span>
                            )}
                          </div>
                        ) : null}
                        <div className="col-category">
                          {product?.category || "-"}
                        </div>
                        <div className="col-price">
                          {Number(product?.price || 0).toFixed(2)} SAR
                        </div>
                        <div className="col-date">
                          {formatDate(product?.createdAt)}
                        </div>
                        <div className="actionIcons">
                          <button
                            type="button"
                            className="action-btn action-btn--delete"
                            onClick={() => handleDeleteConfirm(product?._id)}
                            title="Delete product"
                            aria-label={`Delete ${product?.name || "product"}`}
                          >
                            <i className="fa-regular fa-trash-can" />
                          </button>
                          <Link
                            to={`${editBasePath}/${product?._id}`}
                            className="action-btn action-btn--edit"
                            title="Edit product"
                            aria-label={`Edit ${product?.name || "product"}`}
                          >
                            <i className="fa-regular fa-pen-to-square" />
                          </Link>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
        {showConfirmation && (
          <div className="confirmation-modal">
            <div className="modal-content sec">
              <img src="/photos/question.svg" alt="?" />
              <p>Are you sure you want to delete this product?</p>
              <div className="modal-actions">
                <button onClick={handleDelete}>Yes</button>
                <button onClick={() => setShowConfirmation(false)}>No</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default LastProducts;
